import { createSelector } from 'reselect';
import { Map, List, getIn } from 'immutable';
import memoize from 'memoize-one';

import Business from 'models/BusinessModel';

import {
  ClientSearchFilterTypeLabel,
  ClientSearchFilterType,
  ClientMonitorSearchFilterTypeLabel,
  ClientMonitorSearchFilterType,
} from 'enums/client-search-filter-type';

import { denormalizeModelList } from '../../utils/Normalizer';

const selectMonitorOverview = (state) => state.getIn(['monitor', 'overview']);

export const makeSelectById = () =>
  createSelector(selectMonitorOverview, (state) => state.get('byId', Map()));

const makeSelectClients = () =>
  createSelector(selectMonitorOverview, (state) => state.get('clients', Map()));

const makeSelectManager = () =>
  createSelector(selectMonitorOverview, (state) => state.get('selectedManager'));

const makeSelectClientById = (id) =>
  createSelector(selectMonitorOverview, (state) => state.getIn(['byId', id], new Business()));

const makeSelectClientsList = () =>
  createSelector(
    makeSelectById(),
    makeSelectManager(),
    makeSelectClients(),
    (byId, selectedManager, state) => {
      if (selectedManager.name) {
        return denormalizeModelList(byId, state.get('ids', List())).filter(
          (entity) => entity?.manager?.name === selectedManager.name,
        );
      }

      return denormalizeModelList(byId, state.get('ids', List()));
    },
  );

const makeSelectClientsListWithoutFilters = () =>
  createSelector(makeSelectById(), makeSelectClients(), (byId, state) =>
    denormalizeModelList(byId, state.get('ids', List())),
  );

const makeSelectMonitoredClientsList = () =>
  createSelector(makeSelectClientsList(), (list) =>
    list.filter((entity) => entity.isMonitorConnected),
  );

const makeSelectNonMonitoredClientsList = () =>
  createSelector(makeSelectClientsList(), (list) =>
    list.filter((entity) => !entity.isMonitorConnected),
  );

const makeSelectClientsListSorted = () =>
  createSelector(
    makeSelectMonitoredClientsList(),
    makeSelectNonMonitoredClientsList(),
    (monitored, notMonitored) => monitored.concat(notMonitored),
  );

const makeSelectMonitorHubFilter = () =>
  createSelector(selectMonitorOverview, (state) => state.get('filters', {}));

const searchIncludesTags = (assignedTags, filters) =>
  assignedTags.find(({ id }) => filters.includes(id));

const filterListPropertiesFilters = memoize(({ tags }, searches) =>
  searches.filter(({ assignedTags }) => searchIncludesTags(assignedTags, tags)),
);

const makeFilteredClientsListByStatus = () =>
  createSelector(
    makeSelectClientsListSorted(),
    makeSelectMonitorHubFilter(),
    (sortedClients, filters) => {
      let clientsList = sortedClients;
      const statusFilters = filters.get('status', []).toJS();
      const tagsFilters = filters.get('tags', []).toJS();

      if (statusFilters.length) {
        clientsList = sortedClients.filter((search) =>
          statusFilters.some((filter) => {
            if (filter === ClientSearchFilterTypeLabel[ClientSearchFilterType.allClients]) {
              return true;
            }

            if (filter === ClientSearchFilterTypeLabel[ClientSearchFilterType.recentlyAdded]) {
              return search.isRecentlyAdded;
            }

            if (filter === ClientSearchFilterTypeLabel[ClientSearchFilterType.notConnected]) {
              return !search.isMonitorConnected;
            }

            return sortedClients;
          }),
        );
      }

      if (tagsFilters.length) {
        clientsList = filterListPropertiesFilters({ tags: tagsFilters }, clientsList);
      }

      return clientsList;
    },
  );

const makeFilteredMonitorAlphaClientsListByStatus = () =>
  createSelector(
    makeSelectClientsListSorted(),
    makeSelectMonitorHubFilter(),
    (sortedClients, filters) => {
      let clientsList = sortedClients;
      const statusFilters = filters.get('status', []).toJS();

      if (statusFilters.length) {
        clientsList = sortedClients.filter((client) =>
          statusFilters.some((filter) => {
            if (
              filter ===
              ClientMonitorSearchFilterTypeLabel[ClientMonitorSearchFilterType.allClients]
            ) {
              return true;
            }

            if (
              filter ===
              ClientMonitorSearchFilterTypeLabel[ClientMonitorSearchFilterType.recentlyAdded]
            ) {
              return client.isRecentlyAdded;
            }

            if (
              filter ===
              ClientMonitorSearchFilterTypeLabel[ClientMonitorSearchFilterType.notConnected]
            ) {
              return !client.isMonitorConnected;
            }

            if (
              filter === ClientMonitorSearchFilterTypeLabel[ClientMonitorSearchFilterType.connected]
            ) {
              return client.isMonitorConnected;
            }

            return true;
          }),
        );
      }

      return clientsList;
    },
  );

const makeSelectClientsListFiltered = (predicate) =>
  createSelector(makeSelectClientsListSorted(), (list) => list.filter(predicate));

const makeSelectMonitorRedirectedClientId = () =>
  createSelector(selectMonitorOverview, (state) => state.get('wizzardRedirectClientId', null));

const makeSelectClientsLoading = () =>
  createSelector(makeSelectClients(), (state) => state.getIn(['network', 'loading'], false));

const makeSelectClientsError = () =>
  createSelector(makeSelectClients(), (state) => state.getIn(['network', 'error'], false));

const selectClientIdFromUrl = (_, props) =>
  parseInt(getIn(props, ['match', 'params', 'entityId'], ''), 10);

const makeSelectClientFromUrl = () =>
  createSelector(makeSelectById(), selectClientIdFromUrl, (byId, id) => byId.get(id));

const makeSelectClientsListLoading = () =>
  createSelector(makeSelectClients(), (state) => state.getIn(['listLoading'], false));

export {
  makeSelectClientFromUrl,
  makeSelectClientsList,
  makeSelectClientsListSorted,
  makeSelectManager,
  makeSelectClientsListWithoutFilters,
  makeSelectClientsListFiltered,
  makeSelectClientsLoading,
  makeSelectClientsError,
  makeSelectMonitorHubFilter,
  makeSelectClientsListLoading,
  makeSelectMonitorRedirectedClientId,
  makeFilteredClientsListByStatus,
  makeFilteredMonitorAlphaClientsListByStatus,
  makeSelectClientById,
};
